import React from "react";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

/**
 * The product edit form's first-load skeleton, on the DS `Skeleton` primitive (§ 62
 * grammar, adopted here in § 73): the wait is announced exactly once through a
 * sr-only status, the shapes are decoration, and the shimmer sweep lives in the
 * primitive alone — this replaced ~80 lines of the CSS-class `SkeletonElement`
 * shimmer arranged in a bootstrap grid.
 *
 * The shapes mirror the loaded form: the header row, the Basic Details field grid
 * (label + 36px control pairs on the md control height), and the attribute-chip row.
 */

const FIELD_WIDTHS = ["55%", "70%", "60%"];

const FieldShape: React.FC<{ labelWidth?: string }> = ({ labelWidth = "6rem" }) => (
  <div>
    <Skeleton variant="text" width={labelWidth} />
    <Skeleton variant="rect" height="36px" className="tw-mt-2 tw-rounded-ctl" />
  </div>
);

const ProductEditSkeleton: React.FC<{ "data-testid"?: string }> = ({
  "data-testid": dataTestId,
}) => {
  const baseId = dataTestId || "product-edit-skeleton";
  return (
    /* Left-aligned (2026-09-09): the form it stands in for is left-aligned with the title now,
       so a centred skeleton would jump ~270px to the left the moment the data landed. */
    <div aria-busy="true" data-testid={baseId} className="tw-flex">
      <span role="status" className="tw-sr-only" data-testid={`${baseId}-status`}>
        Loading product details
      </span>
      <div aria-hidden="true" data-testid={`${baseId}-shapes`} className="tw-w-3/4">
        <Skeleton variant="text" width="10rem" className="tw-mt-2" />

        <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-4">
          <FieldShape />
          <FieldShape />
        </div>

        <div className="tw-mt-4">
          <FieldShape labelWidth="4rem" />
        </div>
        <div className="tw-mt-4">
          <Skeleton variant="text" width="6rem" />
          <Skeleton variant="rect" height="80px" className="tw-mt-2 tw-rounded-ctl" />
        </div>

        <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-4">
          <FieldShape />
          <FieldShape />
          <FieldShape />
        </div>

        <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-4">
          {FIELD_WIDTHS.map((w, i) => (
            <div
              // Static layout, not data — the index is the only identity a placeholder has.
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="tw-w-48"
            >
              <Skeleton variant="text" width={w} />
              <Skeleton variant="rect" height="36px" className="tw-mt-2 tw-rounded-ctl" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductEditSkeleton;
